import type { CmmdForgeAutomationRunDetail as RunDetail } from "@cmmd-forge/contracts";
import { ArrowLeftIcon } from "lucide-react";

import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import {
  ForgeAutomationRunActions,
  type ForgeAutomationRunAction,
} from "./ForgeAutomationRunActions";
import {
  humanizeAutomationValue,
  RunArtifacts,
  RunAttempts,
  RunFailureNotice,
  RunMetadata,
  RunTimeline,
} from "./ForgeAutomationRunSections";

export interface ForgeAutomationRunDetailProps {
  readonly detail: RunDetail | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly busyAction: ForgeAutomationRunAction | null;
  readonly onBack: () => void;
  readonly onRefresh: () => void;
  readonly onRetry: () => void;
  readonly onCancel: () => void;
  readonly onApprove: () => void;
  readonly onReject: () => void;
}

function statusTone(status: RunDetail["run"]["status"]): string {
  if (status === "done") return "border-success-border bg-success-surface text-success-foreground";
  if (status === "failed" || status === "blocked") {
    return "border-destructive-border bg-destructive-surface text-destructive";
  }
  if (status === "running" || status === "queued") {
    return "border-hue-sky-border bg-hue-sky-surface text-hue-sky-foreground";
  }
  return "border-border bg-muted text-muted-foreground";
}

function RunDetailSkeleton({ onBack }: { readonly onBack: () => void }) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-background px-5 py-6 text-foreground sm:px-10">
      <button
        type="button"
        className="flex items-center gap-2 text-sm text-muted-foreground"
        onClick={onBack}
      >
        <ArrowLeftIcon className="size-4" />
        Automation
      </button>
      <div className="mx-auto mt-8 max-w-5xl space-y-4">
        <Skeleton className="h-9 w-52" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-52 w-full" />
      </div>
    </div>
  );
}

export function ForgeAutomationRunDetail(props: ForgeAutomationRunDetailProps) {
  if (props.loading && props.detail === null) return <RunDetailSkeleton onBack={props.onBack} />;

  if (props.detail === null) {
    return (
      <div className="min-h-0 flex-1 overflow-y-auto bg-background px-5 py-6 text-foreground sm:px-10">
        <button
          type="button"
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          onClick={props.onBack}
        >
          <ArrowLeftIcon className="size-4" />
          Automation
        </button>
        <div className="mx-auto mt-16 max-w-xl rounded-md border border-destructive-border bg-destructive-surface p-4 text-sm text-destructive">
          {props.error ?? "Run detail is unavailable."}
        </div>
      </div>
    );
  }

  const { run, rule, attempts, artifacts, events } = props.detail;
  const sourceTitle = typeof run.sourceRef["title"] === "string" ? run.sourceRef["title"] : null;

  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-background text-foreground">
      <div className="mx-auto w-full max-w-6xl px-5 py-6 sm:px-10">
        <button
          type="button"
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          onClick={props.onBack}
        >
          <ArrowLeftIcon className="size-4" />
          {rule.name}
        </button>

        <header className="mt-7 flex flex-col gap-5 border-b border-border/60 pb-6 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-semibold tracking-tight">Run #{run.id}</h1>
              <span
                className={cn(
                  "rounded-full border px-2.5 py-1 text-xs capitalize",
                  statusTone(run.status),
                )}
              >
                {humanizeAutomationValue(run.status)}
              </span>
            </div>
            <p className="mt-2 truncate text-sm text-muted-foreground">
              {sourceTitle ?? rule.name} · {humanizeAutomationValue(run.currentStage)} ·{" "}
              {run.autonomyMode}
            </p>
          </div>
          <ForgeAutomationRunActions
            status={run.status}
            loading={props.loading}
            busyAction={props.busyAction}
            onRefresh={props.onRefresh}
            onRetry={props.onRetry}
            onCancel={props.onCancel}
            onApprove={props.onApprove}
            onReject={props.onReject}
          />
        </header>

        {props.error ? (
          <div className="mt-5 rounded-md border border-destructive-border bg-destructive-surface px-3 py-2 text-sm text-destructive">
            {props.error}
          </div>
        ) : null}

        <RunFailureNotice run={run} />

        <div className="mt-7 grid gap-7 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="min-w-0 space-y-8">
            <RunAttempts attempts={attempts} />
            <RunArtifacts artifacts={artifacts} />
            <RunTimeline events={events} />
          </div>
          <RunMetadata detail={props.detail} />
        </div>
      </div>
    </div>
  );
}
